// ============================================================
// Dashboard.jsx
// Step 4: Visual compliance dashboard
// ============================================================

import React from "react";
import {
  calcOverallStats,
  calcCategoryStats,
  rankCategoriesByScore,
  collectImmediateItems,
  scoreToRating,
  scoreToClass,
} from "../utils/calculations";
import { exportToCSV, exportToJSON, exportActionItemsCSV } from "../utils/exportUtils";

export default function Dashboard({ auditInfo, categories, responses, onViewFindings, onBack }) {
  const overall = calcOverallStats(categories, responses);
  const ranked = rankCategoriesByScore(categories, responses);
  const top3Best = [...ranked].reverse().slice(0, 3);
  const top3Worst = ranked.slice(0, 3);
  const immediateItems = collectImmediateItems(categories, responses);

  const totalItems = categories.reduce((s, c) => s + c.criteria.length, 0);

  // Donut chart via SVG
  const DonutChart = ({ score }) => {
    const r = 54;
    const circ = 2 * Math.PI * r;
    const filled = (score / 100) * circ;
    const cls = scoreToClass(score);
    return (
      <svg viewBox="0 0 120 120" className="donut-svg">
        <circle cx="60" cy="60" r={r} fill="none" stroke="var(--border)" strokeWidth="12" />
        <circle
          cx="60" cy="60" r={r} fill="none"
          strokeWidth="12"
          strokeDasharray={`${filled} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
          className={`donut-arc ${cls}`}
        />
        <text x="60" y="56" textAnchor="middle" className="donut-score">{score}%</text>
        <text x="60" y="72" textAnchor="middle" className="donut-label">{scoreToRating(score)}</text>
      </svg>
    );
  };

  // Mini horizontal bar
  const MiniBar = ({ score, cls }) => (
    <div className="mini-bar-track">
      <div className={`mini-bar-fill ${cls}`} style={{ width: `${score}%` }} />
    </div>
  );

  // Status distribution bar
  const DistBar = () => {
    const total = overall.totalAnswered;
    if (total === 0) return <div className="dist-empty">No items reviewed yet.</div>;
    const pC = Math.round((overall.totalCompliant / total) * 100);
    const pP = Math.round((overall.totalPartial / total) * 100);
    const pN = Math.round((overall.totalNonCompliant / total) * 100);
    const pNA = Math.round((overall.totalNA / total) * 100);
    return (
      <div>
        <div className="dist-bar">
          {pC > 0 && <div className="dist-seg dist-compliant" style={{ width: `${pC}%` }} title={`Compliant: ${overall.totalCompliant}`} />}
          {pP > 0 && <div className="dist-seg dist-partial" style={{ width: `${pP}%` }} title={`Partial: ${overall.totalPartial}`} />}
          {pN > 0 && <div className="dist-seg dist-noncompliant" style={{ width: `${pN}%` }} title={`Non-Compliant: ${overall.totalNonCompliant}`} />}
          {pNA > 0 && <div className="dist-seg dist-na" style={{ width: `${pNA}%` }} title={`N/A: ${overall.totalNA}`} />}
        </div>
        <div className="dist-legend">
          <span className="leg-item"><span className="leg-dot dot-compliant" /> Compliant ({overall.totalCompliant})</span>
          <span className="leg-item"><span className="leg-dot dot-partial" /> Partial ({overall.totalPartial})</span>
          <span className="leg-item"><span className="leg-dot dot-noncompliant" /> Non-Compliant ({overall.totalNonCompliant})</span>
          <span className="leg-item"><span className="leg-dot dot-na" /> N/A ({overall.totalNA})</span>
        </div>
      </div>
    );
  };

  return (
    <div className="page-container print-full">
      {/* Print header */}
      <div className="print-report-header">
        <div className="print-company">{auditInfo.companyName || "—"}</div>
        <div className="print-meta">
          {auditInfo.facilityName && <span>{auditInfo.facilityName} · </span>}
          Audit Date: {auditInfo.auditDate} · Auditor: {auditInfo.auditorName || "—"}
        </div>
      </div>

      <div className="page-header no-print">
        <h1 className="page-title">Compliance Dashboard</h1>
        <p className="page-desc">
          {auditInfo.companyName} — {auditInfo.auditDate}
        </p>
      </div>

      {/* Export toolbar */}
      <div className="toolbar no-print">
        <button className="btn btn-outline btn-sm" onClick={() => exportToCSV(auditInfo, categories, responses)}>
          ↓ Export Full CSV
        </button>
        <button className="btn btn-outline btn-sm" onClick={() => exportActionItemsCSV(auditInfo, categories, responses)}>
          ↓ Action Items CSV
        </button>
        <button className="btn btn-outline btn-sm" onClick={() => exportToJSON(auditInfo, categories, responses)}>
          ↓ Export JSON
        </button>
        <button className="btn btn-outline btn-sm" onClick={() => window.print()}>
          🖨 Print / PDF
        </button>
      </div>

      {/* Top KPI cards */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-score">
          <DonutChart score={overall.overallScore} />
          <div className="kpi-card-label">Overall Compliance Score</div>
        </div>

        <div className="kpi-metrics">
          <div className="metric-card">
            <div className="metric-num">{categories.length}</div>
            <div className="metric-label">Programs Audited</div>
          </div>
          <div className="metric-card">
            <div className="metric-num">{overall.totalAnswered}</div>
            <div className="metric-label">Items Reviewed</div>
          </div>
          <div className="metric-card metric-alert">
            <div className="metric-num">{overall.totalNonCompliant}</div>
            <div className="metric-label">Non-Compliant Items</div>
          </div>
          <div className="metric-card metric-warn">
            <div className="metric-num">{overall.totalPartial}</div>
            <div className="metric-label">Partially Compliant</div>
          </div>
          <div className="metric-card">
            <div className="metric-num">{immediateItems.length}</div>
            <div className="metric-label">Immediate Actions</div>
          </div>
          <div className="metric-card">
            <div className="metric-num">{overall.totalItems}</div>
            <div className="metric-label">Total Items ({totalItems})</div>
          </div>
        </div>
      </div>

      {/* Status distribution */}
      <div className="card">
        <h2 className="card-title">Response Distribution</h2>
        <DistBar />
      </div>

      {/* Top 3 best / worst */}
      <div className="two-col">
        <div className="card">
          <h2 className="card-title">✓ Most Compliant Programs</h2>
          {top3Best.length === 0 ? (
            <p className="muted">No scored programs yet.</p>
          ) : (
            top3Best.map(({ cat, stats }) => (
              <div key={cat.id} className="ranked-item">
                <div className="ranked-name">{cat.name}</div>
                <MiniBar score={stats.score} cls={scoreToClass(stats.score)} />
                <div className={`ranked-score ${scoreToClass(stats.score)}`}>{stats.score}%</div>
              </div>
            ))
          )}
        </div>

        <div className="card">
          <h2 className="card-title">⚠ Least Compliant Programs</h2>
          {top3Worst.length === 0 ? (
            <p className="muted">No scored programs yet.</p>
          ) : (
            top3Worst.map(({ cat, stats }) => (
              <div key={cat.id} className="ranked-item">
                <div className="ranked-name">{cat.name}</div>
                <MiniBar score={stats.score} cls={scoreToClass(stats.score)} />
                <div className={`ranked-score ${scoreToClass(stats.score)}`}>{stats.score}%</div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Immediate action items */}
      {immediateItems.length > 0 && (
        <div className="card card-urgent">
          <h2 className="card-title">🚨 Immediate Action Required</h2>
          <p className="card-subtitle">
            The following items require immediate correction. Address before other findings.
          </p>
          <div className="immediate-list">
            {immediateItems.map((item, i) => (
              <div key={i} className="immediate-item">
                <div className="immediate-header">
                  <span className="badge badge-immediate">IMMEDIATE</span>
                  <span className="immediate-citation">{item.citation}</span>
                  <span className="immediate-program">{item.categoryName}</span>
                </div>
                <div className="immediate-req">{item.requirement}</div>
                {item.actionItem && (
                  <div className="immediate-action">→ {item.actionItem}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-category summary table */}
      <div className="card">
        <h2 className="card-title">Program-by-Program Summary</h2>
        <div className="table-wrap">
          <table className="summary-table">
            <thead>
              <tr>
                <th>Program</th>
                <th>Citation</th>
                <th className="text-center">Score</th>
                <th className="text-center">✓</th>
                <th className="text-center">◑</th>
                <th className="text-center">✗</th>
                <th className="text-center">N/A</th>
                <th className="text-center">Reviewed</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => {
                const s = calcCategoryStats(cat, responses);
                return (
                  <tr key={cat.id}>
                    <td className="td-program">{cat.name}</td>
                    <td className="td-citation">{cat.citation}</td>
                    <td className="text-center">
                      {s.score !== null ? (
                        <span className={`score-pill ${scoreToClass(s.score)}`}>{s.score}%</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="text-center text-compliant">{s.compliant}</td>
                    <td className="text-center text-partial">{s.partial}</td>
                    <td className="text-center text-noncompliant">{s.nonCompliant}</td>
                    <td className="text-center text-na">{s.notApplicable}</td>
                    <td className="text-center">{s.answered}/{s.total}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="btn-row no-print">
        <button className="btn btn-secondary" onClick={onBack}>← Back to Audit</button>
        <button className="btn btn-primary btn-lg" onClick={onViewFindings}>
          View Findings & Action Items →
        </button>
      </div>
    </div>
  );
}
